import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { normalizeFilter } from '../query/codec'
import { describeCondition } from '../query/describe'
import type { Condition, Filter, Register } from '../query/types'

function FilterChip({ text, onRemove }: { text: string; onRemove: () => void }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted py-0.5 pr-1 pl-3 text-sm">
      {/* On a phone the whole text wraps (the days a period covers are the point of the chip); wider, it is cut off. */}
      <span className="break-words md:truncate" title={text}>
        {text}
      </span>
      <Button type="button" variant="ghost" size="icon-xs" aria-label={`Filter ${text} weghalen`} onClick={onRemove}>
        <X />
      </Button>
    </span>
  )
}

/**
 * The line "Actief: …" under the filter bar (design §4.2, addendum A.4.3): a chip per quick
 * condition and per group, each with an ×, and "Wis filters". A relative date shows the days it
 * covers. "Wis filters" takes every condition away, those of the view too; "Herstellen" is the
 * way back to the view.
 */
export function ActiveFilters({
  filter,
  register,
  names,
  today,
  onChange,
}: {
  filter: Filter
  register: Register
  /** Names of the ids in list values (labels, categories, the team, organisations, persons). */
  names: ReadonlyMap<string, string>
  today: string
  onChange: (next: Filter) => void
}) {
  if (filter.quick.length === 0 && filter.groups.length === 0) return null
  const text = (condition: Condition): string =>
    describeCondition(condition, register.properties.find((property) => property.key === condition.property), { names, today })
  return (
    <div role="group" aria-label="Actieve filters" className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">Actief:</span>
      {filter.quick.map((condition, index) => (
        <FilterChip
          key={`quick-${index}-${condition.property}-${condition.operator}`}
          text={text(condition)}
          onRemove={() => onChange(normalizeFilter({ ...filter, quick: filter.quick.filter((_, at) => at !== index) }))}
        />
      ))}
      {filter.groups.map((group, index) => {
        const body = group.map(text).join(' en ')
        return (
          <FilterChip
            key={`group-${index}`}
            text={index === 0 ? body : `OF-groep ${index + 1}: ${body}`}
            onRemove={() => onChange(normalizeFilter({ ...filter, groups: filter.groups.filter((_, at) => at !== index) }))}
          />
        )
      })}
      <Button type="button" variant="link" size="sm" className="ml-auto" onClick={() => onChange(normalizeFilter({}))}>
        Wis filters
      </Button>
    </div>
  )
}
